import {useContext} from 'react';

import {ChartOptions, ChartData, ChartDataset} from 'chart.js';
import {Line} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';

import {Grid, SxProps, Typography} from '@mui/material';

import {bodyPdf, pdfH1, pdfH4} from './styles';
import {CultureAnalysisPdfContext} from '../../../contexts/CultureAnalysisPdfContext/CultureAnalysisPdfContext';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors, empionOrangeGradient} from '../../../layouts/theme/designSystem';
import {serializeKey} from '../../../utils/serializer/serialize';

const PillarChart = ({data}: {data: ChartData<'line'>}) => {
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: true,
    devicePixelRatio: 5,
    showLine: true,
    plugins: {
      tooltip: {
        enabled: false,
      },
      // disables the top legend (will be added manually as part of the text)
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        max: 6,
        min: 0,
        ticks: {
          mirror: false,
          labelOffset: 0,
          padding: 20,
          color: greenColors[700],
          stepSize: 1,
          maxTicksLimit: 10, // increase space between the max and min ranges
        },
        grid: {
          display: false,
        },
        clip: false,
        offset: true, // avoid clipping the points
      },
      x: {
        ticks: {
          backdropColor: orangeColors[3],
          color: greenColors[700],
          backdropPadding: 8,
          showLabelBackdrop: true,
          maxRotation: 90,
          minRotation: 90,
          labelOffset: -7,
          font: {
            size: 12,
            family: 'monospace', // changed the font to monospace to make the padding work
          },
        },
        grid: {
          color: orangeColors[3],
          lineWidth: 30,
        },
        border: {
          color: greenColors[700],
          width: 5,
        },
      },
    },
  };

  return <Line style={{}} data={data} options={options} />;
};

export const PillarComparisons = () => {
  const {t} = useTranslation('pdfDomain');
  const currentLanguage = useContext(LanguageContext);

  const {data, loading: isLoading} = useContext(CultureAnalysisPdfContext);

  if (isLoading || !data) {
    return null;
  }

  const averageAnswersData = data.companySurveyAverageAnswers;

  const defaultDataSetOpts = {
    pointBorderWidth: 8,
    borderWidth: 2,
  };

  const legendIconStyles: SxProps = {
    width: '8px',
    height: '20px',
    borderRadius: 2,
  };

  const labels: string[] = [];
  const datasets: ChartDataset<'line'>[] = [
    // Management
    {
      data: [],
      borderColor: orangeColors[900],
      ...defaultDataSetOpts,
    },
    // Employee
    {
      data: [],
      borderColor: greenColors[700],
      ...defaultDataSetOpts,
    },
  ];

  // Create labels and datasets data out of the data response

  const pillarTitles = data.cultureHouse;
  Object.entries(averageAnswersData.averageByPillar).map(([key, value]) => {
    // The keys in the survey response are camelcased due to the serializer, so we need them back to snake case
    // to match the pillar titles
    labels.push(
      pillarTitles.find((pillar) => pillar.key === serializeKey('SnakeCase', key))?.pillarTitle[currentLanguage] || key
    );

    datasets[0].data.push(value.management);
    datasets[1].data.push(value.employee);
  });

  const maxLengthChartLabel = labels.reduce((acc, curr) => (acc > curr.length ? acc : curr.length), 0);
  const paddedChartLabels = labels.map((label) => label.padEnd(maxLengthChartLabel, ' '));

  const highestCulturePillar =
    pillarTitles.find((pillar) => pillar.key === serializeKey('SnakeCase', averageAnswersData.highestOverlapPillar))
      ?.pillarTitle[currentLanguage] || averageAnswersData.highestOverlapPillar;

  const lowestCulturePillar =
    pillarTitles.find((pillar) => pillar.key === serializeKey('SnakeCase', averageAnswersData.lowestOverlapPillar))
      ?.pillarTitle[currentLanguage] || averageAnswersData.lowestOverlapPillar;

  return (
    <>
      <Grid container p={5} sx={{background: empionOrangeGradient.dark}}>
        <Typography variant='h1' sx={pdfH1}>
          {t('pillarComparisons.title')}
        </Typography>
        <Typography sx={bodyPdf} mt={2}>
          {t('pillarComparisons.textBlock')}
        </Typography>
      </Grid>

      <Grid container px={5}>
        <Grid item container minHeight={'480px'} width={'650px'} justifyContent={'center'}>
          <PillarChart
            data={{
              labels: paddedChartLabels,
              datasets,
            }}
          />
        </Grid>

        <Grid item xs={12} mt={5}>
          <Typography variant='h4' sx={pdfH4} mb={1}>
            {t('pillarComparisons.highestOverlap', {highestCulturePillar: highestCulturePillar})}
          </Typography>
          <Typography sx={bodyPdf}>{t('pillarComparisons.highestOverlapDescription')}</Typography>

          <Typography variant='h4' sx={pdfH4} mt={3} mb={1}>
            {t('pillarComparisons.lowestOverlap', {lowestCulturePillar: lowestCulturePillar})}
          </Typography>
          <Typography sx={bodyPdf}>{t('pillarComparisons.lowestOverlapDescription')}</Typography>
        </Grid>

        <Grid item container mt={5}>
          <Grid item mb={1} container flexWrap={'nowrap'}>
            <Grid item sx={legendIconStyles} bgcolor={greenColors[700]} />
            <Typography sx={bodyPdf} ml={1}>
              {t('shared.employees')}
            </Typography>
          </Grid>
          <Grid item container flexWrap={'nowrap'}>
            <Grid item sx={legendIconStyles} bgcolor={orangeColors[900]} />
            <Typography sx={bodyPdf} ml={1}>
              {t('shared.management')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
